import { publicApi } from "../../../services/public-api"
import type { AudioResult } from "../../../components/Education/EducationAudioPlayer.tsx"
import type {
  PublicErrorPayload,
  PublicGuide,
  PublicMoreAccepted,
  PublicMoreStatus,
  PublicResolveResult,
  PublicVerifyCheckResult
} from "../types/public-education.types"
import { withEducationSession } from "./public-education.client"

const ROOT = "/project-education/public"

export interface PublicEducationResponse<T> {
  ok: boolean
  status: number
  data: T | null
  error: PublicErrorPayload | null
}

// Responses here are never enveloped (see the types file's header comment),
// so this is a straight status split — no `unwrapEnvelope`.
const asResponse = <T>(status: number, body: unknown): PublicEducationResponse<T> =>
  status >= 200 && status < 300
    ? { ok: true, status, data: (body ?? null) as T, error: null }
    : { ok: false, status, data: null, error: (body ?? { code: "unknown" }) as PublicErrorPayload }

// `publicApi`'s `validateStatus: status < 500` means only a real network
// failure or a 5xx ever lands here.
const asFailure = <T>(): PublicEducationResponse<T> => ({
  ok: false,
  status: 0,
  data: null,
  error: { code: "unknown" }
})

/** The token goes in a body, never a URL or a query string — the page reads
 *  it from the fragment, which no browser sends to a server, and putting it
 *  back into the address would undo that in one step. */
export const resolveEducationLink = async (
  token: string
): Promise<PublicEducationResponse<PublicResolveResult>> => {
  try {
    const { status, data } = await publicApi.post(`${ROOT}/resolve/`, { token })
    return asResponse<PublicResolveResult>(status, data)
  } catch {
    return asFailure()
  }
}

/** Always resolves `ok: true` on a real answer — the endpoint itself always
 *  answers 202, matching or not, so this never reveals whether the address
 *  was recognized. */
export const sendEducationVerificationCode = async (
  session: string,
  email: string
): Promise<PublicEducationResponse<null>> => {
  try {
    const { status, data } = await publicApi.post(
      `${ROOT}/verify/send/`,
      { email },
      withEducationSession(session)
    )
    return asResponse<null>(status, data)
  } catch {
    return asFailure()
  }
}

export const checkEducationVerificationCode = async (
  session: string,
  code: string
): Promise<PublicEducationResponse<PublicVerifyCheckResult>> => {
  try {
    const { status, data } = await publicApi.post(
      `${ROOT}/verify/check/`,
      { code },
      withEducationSession(session)
    )
    return asResponse<PublicVerifyCheckResult>(status, data)
  } catch {
    return asFailure()
  }
}

export const fetchEducationGuide = async (
  session: string
): Promise<PublicEducationResponse<PublicGuide>> => {
  try {
    const { status, data } = await publicApi.get(`${ROOT}/guide/`, withEducationSession(session))
    return asResponse<PublicGuide>(status, data)
  } catch {
    return asFailure()
  }
}

export interface RequestMorePublicData {
  focus_code?: string
  question?: string
}

export const requestEducationMore = async (
  session: string,
  idempotencyKey: string,
  body: RequestMorePublicData
): Promise<PublicEducationResponse<PublicMoreAccepted>> => {
  try {
    const { status, data } = await publicApi.post(`${ROOT}/more/`, body, {
      headers: {
        "X-Education-Session": session,
        "Idempotency-Key": idempotencyKey
      }
    })
    return asResponse<PublicMoreAccepted>(status, data)
  } catch {
    return asFailure()
  }
}

export const fetchEducationMoreStatus = async (
  session: string,
  publicId: string
): Promise<PublicEducationResponse<{ status: PublicMoreStatus }>> => {
  try {
    const { status, data } = await publicApi.get(
      `${ROOT}/more/${encodeURIComponent(publicId)}/`,
      withEducationSession(session)
    )
    return asResponse<{ status: PublicMoreStatus }>(status, data)
  } catch {
    return asFailure()
  }
}

interface PublicAudioPayload {
  status: string
  url?: string | null
  duration_ms?: number | null
}

/** Adapts `public/audio/`'s payload into the shape `EducationAudioPlayer`
 *  already reads (`AudioResult` has no "expired" — an expired asset needs
 *  the reader to press Play again, which the player's own `failed` state
 *  already offers via Retry). */
const toAudioResult = (payload: PublicAudioPayload | null): AudioResult => {
  if (payload?.status === "ready" && payload.url && payload.duration_ms != null) {
    return { status: "ready", url: payload.url, duration_ms: payload.duration_ms }
  }
  if (payload?.status === "queued" || payload?.status === "synthesizing") {
    return { status: payload.status }
  }
  return { status: "failed" }
}

/** `EducationAudioPlayer`'s `fetchAudio` — polled by the player itself while
 *  synthesizing, so a caller only needs to hand this straight through. */
export const fetchEducationPublicAudio = async (
  session: string,
  publicId: string
): Promise<AudioResult> => {
  try {
    const { data } = await publicApi.post(
      `${ROOT}/audio/`,
      { public_id: publicId },
      withEducationSession(session)
    )
    return toAudioResult(data as PublicAudioPayload | null)
  } catch {
    return { status: "failed" }
  }
}

export type PublicEducationEventType =
  | "audio_started"
  | "audio_completed"
  | "audio_progress"
  | "contact_clicked"
  | "more_opened"

export interface PublicEducationEvent {
  type: PublicEducationEventType
  public_id?: string
  position_ms?: number
}

/** Fire-and-forget, on purpose: an analytics beacon must never surface an
 *  error to the reader, retry, or block whatever they were doing (following
 *  a mailto: link, pausing audio). Never awaited by any caller. */
export const sendEducationPublicEvent = (session: string, event: PublicEducationEvent): void => {
  publicApi.post(`${ROOT}/events/`, event, withEducationSession(session)).catch(() => {
    // Silent by design — see the doc comment above.
  })
}
